import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, Database, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { reindexKnowledgeBase, getKnowledgeStats } from "@/lib/rag.functions";

export const Route = createFileRoute("/admin/knowledge")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({ meta: [{ title: "AI Knowledge Base · MAKAI" }] }),
  component: Page,
});

function Page() {
  const fetchStats = useServerFn(getKnowledgeStats);
  const reindex = useServerFn(reindexKnowledgeBase);
  const [busy, setBusy] = useState(false);

  const stats = useQuery({
    queryKey: ["rag-stats"],
    queryFn: () => fetchStats(),
  });

  const run = async () => {
    setBusy(true);
    const t = toast.loading("Indexing school resources…");
    try {
      const r = await reindex();
      toast.success(`Indexed ${r.embedded}/${r.chunks} chunks from ${r.rows} resources`, { id: t });
      stats.refetch();
    } catch (e) {
      toast.error((e as Error).message ?? "Indexing failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const total = stats.data?.total ?? 0;
  const byType = stats.data?.byType ?? {};

  return (
    <AppShell role="admin" title="AI Knowledge Base" subtitle="Index school resources so MAKAI can cite them when answering">
      <div className="mx-auto max-w-3xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Retrieval-Augmented Tutor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>
              MAKAI searches your school's Lessons, Digital Library, Curriculum subjects, Assignments and Quizzes before
              every answer, and cites the exact source it used. When no school resource matches, MAKAI falls back to
              general knowledge and clearly labels the response.
            </p>
            <p>Reindex whenever teachers add or update lessons, quizzes or library items.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" /> Index status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {stats.isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <div className="text-3xl font-semibold">{total.toLocaleString()} <span className="text-base font-normal text-muted-foreground">indexed chunks</span></div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(byType).map(([k, v]) => (
                    <Badge key={k} variant="secondary" className="text-xs">{k}: {v}</Badge>
                  ))}
                  {!Object.keys(byType).length && (
                    <span className="text-sm text-muted-foreground">No resources indexed yet.</span>
                  )}
                </div>
              </>
            )}
            <Button onClick={run} disabled={busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {busy ? "Indexing…" : "Reindex now"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
